// Intl only knows a zone's abbreviation (EDT, IST, SAST…) in the locales that
// use it — en-US gives "EDT" but "GMT+5:30" for India. So try the browser's own
// locale first, then a few English locales, and only fall back to the GMT+x form
// when none of them has a real abbreviation.
const TZ_LOCALES = [undefined, "en-US", "en-GB", "en-IN", "en-AU", "en-ZA", "en-CA", "en-NZ"];
const isOffsetStyle = (name) => /^(GMT|UTC)[+\-\u2212]/.test(name);

const getTimeZoneAbbreviation = (date, timeZone) => {
  let fallback = "";
  for (const locale of TZ_LOCALES) {
    const name = new Intl.DateTimeFormat(locale, { timeZone, timeZoneName: "short" })
      .formatToParts(date)
      .find((part) => part.type === "timeZoneName")?.value;
    if (name && !isOffsetStyle(name)) return name;
    fallback = fallback || name || "";
  }
  return fallback;
};

const dayKey = (date, timeZone) => date.toLocaleDateString("en-CA", { timeZone }); // YYYY-MM-DD in that zone

// Last activity as two scannable parts, in the viewer's own browser timezone:
//   date: "Today" / "Yesterday" / "5 Oct 2026"
//   time: "1:29 PM IST"  (12-hour with AM/PM and a real zone abbreviation)
// The API sends UTC; a timestamp with no zone designator is treated as UTC rather
// than read as local time. `timeZone` and `now` exist for tests — they default to
// the browser's zone and the current time.
export const formatActivityParts = (iso, timeZone, now = new Date()) => {
  const hasZone = /(Z|[+-]\d{2}:?\d{2})$/i.test(iso);
  const date = new Date(hasZone ? iso : `${iso}Z`);

  const key = dayKey(date, timeZone);
  const yesterday = new Date(now.getTime() - 24 * 60 * 60 * 1000);
  const dateLabel =
    key === dayKey(now, timeZone)
      ? "Today"
      : key === dayKey(yesterday, timeZone)
        ? "Yesterday"
        : date.toLocaleDateString("en-GB", { timeZone, day: "numeric", month: "short", year: "numeric" });

  const time = date.toLocaleTimeString("en-US", { timeZone, hour: "numeric", minute: "2-digit", hour12: true });
  return { date: dateLabel, time: `${time} ${getTimeZoneAbbreviation(date, timeZone)}`.trim() };
};

// "Today, 7:54 PM IST" / "5 Oct 2026, 1:29 PM IST" — one-line form for tooltips and captions.
export const formatLocalDateTime = (iso, timeZone, now = new Date()) => {
  const { date, time } = formatActivityParts(iso, timeZone, now);
  return `${date}, ${time}`;
};
